// @ts-nocheck — legacy HealthScribe S3 types vs API drift (strict-check noise).
import {
  IHealthScribeRepository,
  IS3MultipartUploadInit,
  IS3MultipartUploadInitResponse,
  IS3MultipartUploadPart,
  IS3MultipartUploadComplete,
  IS3MultipartUploadCompleteResponse,
  IAudioChunk,
} from "../../../core/ports/HealthScribe";

export interface IS3UploadProvider {
  // Upload management
  startUpload(
    sessionId: string,
    filename: string,
    contentType: string
  ): Promise<void>;
  uploadChunk(chunk: IAudioChunk): Promise<void>;
  completeUpload(): Promise<{ location: string; bucket: string; key: string }>;
  abortUpload(): Promise<void>;

  // Status
  isUploading(): boolean;
  getUploadProgress(): {
    uploadedParts: number;
    totalParts: number;
    totalBytes: number;
  };
}

export class HealthScribeS3UploadProvider implements IS3UploadProvider {
  private repository: IHealthScribeRepository;
  private uploadId: string | null = null;
  private key: string | null = null;
  private bucket: string | null = null;
  private sessionId: string | null = null;
  private contentType: string | null = null;
  private parts: IS3MultipartUploadPart[] = [];
  private currentPartNumber = 1;
  private chunkBuffer: Blob[] = [];
  private maxChunkSize = 5 * 1024 * 1024; // 5MB per part (S3 minimum)
  private currentChunkSize = 0;
  private totalBytes = 0;

  constructor(repository: IHealthScribeRepository) {
    this.repository = repository;
  }

  async startUpload(
    sessionId: string,
    filename: string,
    contentType: string
  ): Promise<void> {
    try {
      this.sessionId = sessionId;
      this.contentType = contentType;

      const initRequest: IS3MultipartUploadInit = {
        sessionId,
        filename,
        contentType,
      };

      const response: IS3MultipartUploadInitResponse =
        await this.repository.initS3MultipartUpload(initRequest);

      this.uploadId = response.uploadId;
      this.key = response.key;
      this.bucket = response.bucket;
      this.parts = [];
      this.currentPartNumber = 1;
      this.chunkBuffer = [];
      this.currentChunkSize = 0;
      this.totalBytes = 0;

      console.log("[S3 Upload Provider] Started multipart upload:", {
        sessionId,
        uploadId: this.uploadId,
        key: this.key,
        bucket: this.bucket,
      });
    } catch (error) {
      console.error("[S3 Upload Provider] Failed to start upload:", error);
      throw new Error(`Failed to start S3 multipart upload: ${error}`);
    }
  }

  async uploadChunk(chunk: IAudioChunk): Promise<void> {
    if (!this.uploadId || !this.sessionId) {
      throw new Error("Upload not started. Call startUpload() first.");
    }

    try {
      // Add chunk to buffer
      this.chunkBuffer.push(chunk.data);
      this.currentChunkSize += chunk.data.size;
      this.totalBytes += chunk.data.size;

      // If we've accumulated enough data, upload as a part
      if (this.currentChunkSize >= this.maxChunkSize) {
        await this.uploadCurrentPart();
      }
    } catch (error) {
      console.error("[S3 Upload Provider] Failed to upload chunk:", error);
      throw new Error(`Failed to upload chunk: ${error}`);
    }
  }

  private async uploadCurrentPart(): Promise<void> {
    if (!this.uploadId || !this.sessionId || this.chunkBuffer.length === 0) {
      return;
    }

    try {
      // Combine chunks into a single blob
      const partBlob = new Blob(this.chunkBuffer, {
        type: this.contentType || "audio/webm",
      });

      console.log(
        `[S3 Upload Provider] Uploading part ${this.currentPartNumber}:`,
        {
          size: partBlob.size,
          chunks: this.chunkBuffer.length,
        }
      );

      // Upload the part
      const response = await this.repository.uploadS3MultipartPart(
        this.sessionId,
        this.uploadId,
        this.currentPartNumber,
        partBlob
      );

      // Store part info
      this.parts.push({
        partNumber: this.currentPartNumber,
        etag: response.etag,
      });

      // Reset for next part
      this.currentPartNumber++;
      this.chunkBuffer = [];
      this.currentChunkSize = 0;

      console.log(
        `[S3 Upload Provider] Successfully uploaded part ${
          this.currentPartNumber - 1
        }`
      );
    } catch (error) {
      console.error("[S3 Upload Provider] Failed to upload part:", error);
      throw new Error(
        `Failed to upload part ${this.currentPartNumber}: ${error}`
      );
    }
  }

  async completeUpload(): Promise<{
    location: string;
    bucket: string;
    key: string;
  }> {
    if (!this.uploadId || !this.sessionId || !this.key || !this.bucket) {
      throw new Error("Upload not started. Call startUpload() first.");
    }

    try {
      // Upload any remaining chunks as the final part
      if (this.chunkBuffer.length > 0) {
        await this.uploadCurrentPart();
      }

      if (this.parts.length === 0) {
        throw new Error("No parts uploaded. Cannot complete upload.");
      }

      const completeRequest: IS3MultipartUploadComplete = {
        uploadId: this.uploadId,
        parts: this.parts,
        key: this.key,
        bucket: this.bucket,
      };

      const response: IS3MultipartUploadCompleteResponse =
        await this.repository.completeS3MultipartUpload(completeRequest);

      console.log("[S3 Upload Provider] Successfully completed upload:", {
        location: response.location,
        bucket: response.bucket,
        key: response.key,
        totalParts: this.parts.length,
        totalBytes: this.totalBytes,
      });

      // Reset state
      this.reset();

      return {
        location: response.location,
        bucket: response.bucket,
        key: response.key,
      };
    } catch (error) {
      console.error("[S3 Upload Provider] Failed to complete upload:", error);
      throw new Error(`Failed to complete S3 multipart upload: ${error}`);
    }
  }

  async abortUpload(): Promise<void> {
    if (!this.uploadId || !this.sessionId) {
      return; // Nothing to abort
    }

    try {
      await this.repository.abortS3MultipartUpload(
        this.sessionId,
        this.uploadId
      );
      console.log(
        "[S3 Upload Provider] Successfully aborted upload:",
        this.uploadId
      );
    } catch (error) {
      console.error("[S3 Upload Provider] Failed to abort upload:", error);
      // Don't throw - we want to reset state even if abort fails
    } finally {
      this.reset();
    }
  }

  isUploading(): boolean {
    return this.uploadId !== null;
  }

  getUploadProgress(): {
    uploadedParts: number;
    totalParts: number;
    totalBytes: number;
  } {
    return {
      uploadedParts: this.parts.length,
      totalParts: this.parts.length + (this.chunkBuffer.length > 0 ? 1 : 0),
      totalBytes: this.totalBytes,
    };
  }

  private reset(): void {
    this.uploadId = null;
    this.key = null;
    this.bucket = null;
    this.sessionId = null;
    this.contentType = null;
    this.parts = [];
    this.currentPartNumber = 1;
    this.chunkBuffer = [];
    this.currentChunkSize = 0;
    this.totalBytes = 0;
  }
}
